import {commitMutation, ConnectionHandler, graphql} from 'react-relay'
import type {Environment, RecordSourceProxy} from 'relay-runtime'

import {BLOCKED_BY_LIST_ALL_RELAY_CONNECTION} from '../components/sections/relations-section/LazyRelationshipsBlockedByListView'
import {BLOCKING_LIST_ALL_RELAY_CONNECTION} from '../components/sections/relations-section/LazyRelationshipsBlockingListView'
import type {
  AddBlockedByInput,
  addBlockedByMutation,
  addBlockedByMutation$data,
} from './__generated__/addBlockedByMutation.graphql'

export type OptimisticDependencyIssue = {
  id: string
  title: string
  url: string
  number: number
  state: string
  stateReason?: string | null
  repository: {
    id: string
    nameWithOwner: string
  }
}

function optimisticAddBlockedBy(
  store: RecordSourceProxy,
  input: AddBlockedByInput,
  optimisticIssue: OptimisticDependencyIssue,
  direction: 'blockedBy' | 'blocking',
) {
  // Determine which issue record to update based on the direction:
  // - blockedBy: update the issueId's topBlockedBy connection
  // - blocking: update the blockingIssueId's topBlocking connection
  const viewerIssueId = direction === 'blockedBy' ? input.issueId : input.blockingIssueId
  const issueRecord = store.get(viewerIssueId)
  if (!issueRecord) return

  // Get or create the dependency issue record
  let dependencyRecord = store.get(optimisticIssue.id)
  if (!dependencyRecord) {
    dependencyRecord = store.create(optimisticIssue.id, 'Issue')
  }

  // Set scalar fields on the dependency issue
  dependencyRecord.setValue(optimisticIssue.title, 'title')
  // IssuePickerItem doesn't include titleHTML, so use title as the optimistic value;
  // the server response will overwrite with the real HTML when the mutation resolves.
  dependencyRecord.setValue(optimisticIssue.title, 'titleHTML')
  dependencyRecord.setValue(optimisticIssue.url, 'url')
  dependencyRecord.setValue(optimisticIssue.number, 'number')
  dependencyRecord.setValue(optimisticIssue.state, 'state')
  dependencyRecord.setValue(optimisticIssue.stateReason ?? null, 'stateReason')

  // Set repository on the dependency issue
  let repoRecord = store.get(optimisticIssue.repository.id)
  if (!repoRecord) {
    repoRecord = store.create(optimisticIssue.repository.id, 'Repository')
  }
  repoRecord.setValue(optimisticIssue.repository.nameWithOwner, 'nameWithOwner')
  const [owner, name] = optimisticIssue.repository.nameWithOwner.split('/')
  repoRecord.setValue(name, 'name')

  let ownerRecord = repoRecord.getLinkedRecord('owner')
  if (!ownerRecord) {
    ownerRecord = store.create(`${optimisticIssue.repository.id}:owner:optimistic`, 'Organization')
    repoRecord.setLinkedRecord(ownerRecord, 'owner')
  }
  ownerRecord.setValue(owner, 'login')

  dependencyRecord.setLinkedRecord(repoRecord, 'repository')

  // Append the dependency issue to the appropriate connection
  const connectionField = direction === 'blockedBy' ? 'blockedBy' : 'blocking'
  const connection = issueRecord.getLinkedRecord(connectionField, {first: 3, ranked: true})
  if (connection) {
    const existingNodes = connection.getLinkedRecords('nodes') || []
    if (!existingNodes.some(node => node?.getDataID() === optimisticIssue.id)) {
      connection.setLinkedRecords([...existingNodes, dependencyRecord], 'nodes')
    }
  }

  // Update issueDependenciesSummary
  const summary = issueRecord.getLinkedRecord('issueDependenciesSummary')
  if (summary) {
    const currentCount = (summary.getValue(connectionField) as number) || 0
    summary.setValue(currentCount + 1, connectionField)
  }
}

export function addBlockedByMutation({
  environment,
  input,
  onError,
  onCompleted,
  optimisticBlockingIssue,
  optimisticDirection,
}: {
  environment: Environment
  input: AddBlockedByInput
  onError?: (error: Error) => void
  onCompleted?: (response: addBlockedByMutation$data) => void
  optimisticBlockingIssue?: OptimisticDependencyIssue
  optimisticDirection?: 'blockedBy' | 'blocking'
}) {
  return commitMutation<addBlockedByMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation addBlockedByMutation($input: AddBlockedByInput!) @raw_response_type {
        addBlockedBy(input: $input) {
          issue {
            ...RelationshipsSectionFragment
          }
          blockingIssue {
            id
            number
            title
            repository {
              nameWithOwner
            }
            ...RelationshipsSectionFragment
          }
        }
      }
    `,
    variables: {
      input,
    },
    optimisticUpdater:
      optimisticBlockingIssue && optimisticDirection
        ? store => optimisticAddBlockedBy(store, input, optimisticBlockingIssue, optimisticDirection)
        : undefined,
    onError: error => onError && onError(error),
    onCompleted: response => onCompleted && onCompleted(response),
    updater: store => {
      store.get(input.issueId)?.getLinkedRecord('blockedBy', {first: 100})?.invalidateRecord()
      store.get(input.blockingIssueId)?.getLinkedRecord('blocking', {first: 100})?.invalidateRecord()

      const blockedByDataId = ConnectionHandler.getConnectionID(input.issueId, BLOCKED_BY_LIST_ALL_RELAY_CONNECTION)
      store.get(blockedByDataId)?.invalidateRecord()

      const blockingDataId = ConnectionHandler.getConnectionID(
        input.blockingIssueId,
        BLOCKING_LIST_ALL_RELAY_CONNECTION,
      )
      store.get(blockingDataId)?.invalidateRecord()
    },
  })
}
